import { CustomColorSupport, StepHandle } from "..";
import { TextErrorData, textStepService } from "../../services/steps/text";
import { ColorOption, Step, TextFillImage, TextStepData, TextStepStorage } from "../../types";
import { WorkflowManager, InformationMessageType } from "../../WorkflowManager";
import { Variant } from "../resource/variant";

export interface TextChangeResult {
    /**
     * The text of the input field.
     */
    input: string;

    /**
     * Data that can be used by the theme to decide what helper text should be.
     */
    helperData: {
        charactersRemaining?: number;
    };

    /**
     * Data that can be used by the theme to decide what error text should be.
     */
    errorData?: TextErrorData;
}

export class TextStepHandle extends StepHandle<TextStepData> implements CustomColorSupport {
    constructor(manager: WorkflowManager, step: Step<TextStepData>, tags: string[]) {
        super(manager, step, tags);
    }

    /**
     * Allows for select a font from the available fonts configured on this steps base option.
     * @param variant The font variant to use.
     */
    async selectVariant(variant: Variant) {
        textStepService.selectVariant(
            this.step,
            variant.getResource(),
            this.manager,
            (error) => {
                if (error) {
                    this.manager.setInformationResults([
                        ...this.manager.getInformationResults(),
                        {
                            message: `An issue has occurred with text`,
                            messageType: InformationMessageType.Error,
                            stepID: this.step.stepName,
                        },
                    ]);
                } else {
                    const results = this.manager.getInformationResults().filter((r) => r.stepID !== this.step.stepName);
                    this.manager.setInformationResults(results);
                }
            },
            () => {
                // noop
            },
        );
    }

    /**
     * @returns A list of colors that can be used to fill the text.
     */
    getAvailableFillColors(): ColorOption[] {
        if (!this.step.data.colorPickerEnabled) {
            return [];
        }
        return textStepService.availableFillColors(this.step);
    }

    /**
     * Changes the fill of text related to this step to the new fill value.
     * @param fill A new fill value to use.
     */
    async setFillColor(fill: ColorOption) {
        const stepElements = this.manager.getRegionElements(this.step.stepName);
        await textStepService.changeFillColor(this.step, fill, stepElements, this.manager);
    }

    /**
     * Gets the color currently applied to the element.
     */
    getFillColor() {
        return this.manager.getStepStorage(this.step.stepName)?.color || "#000000";
    }

    getAvailableFillImages(): Promise<TextFillImage[]> {
        return textStepService.availableFillImages(this.step);
    }

    async setFillImage(fillImage: TextFillImage) {
        const stepElements = this.manager.getRegionElements(this.step.stepName);
        await textStepService.changeFillImage(this.step, fillImage, stepElements, this.manager);
    }

    getFillImage(): TextFillImage | undefined {
        return this.manager.getStepStorage(this.step.stepName)?.fillImage;
    }

    getAlignment(): string | undefined {
        const elements = textStepService.findLayoutElements(this.manager, this.step);
        return elements?.[0].align;
    }

    setAlignment(alignment: "left" | "center" | "right") {
        const stepElements = this.manager.getRegionElements(this.step.stepName);
        textStepService.changeAlignment(this.step, alignment, stepElements, this.manager);
    }

    /**
     * When true & the step has replaceable text configured the user will be
     * abled to edit the entire text string including the non-replaceable text.
     * @param shouldCustomizeAll When true the user can configure the entire text string.
     */
    setFullTextCustomization(shouldCustomizeAll: boolean): TextChangeResult {
        const inputText = this.manager.getStepStorage(this.step.stepName)?.inputText || "";

        const customiseableText =
            this.step.data.replaceableText !== undefined
                ? this.step.data.replaceableText.replace("{{}}", inputText)
                : inputText;

        const text = shouldCustomizeAll ? customiseableText : this.step.data.defaultText;
        this.manager.updateStorage(this.step.stepName, {
            customiseAllText: shouldCustomizeAll,
            text,
            defaultCleared: false,
        });
        const result = this.setText(text);
        return result;
    }

    /**
     * Changes the text value of text related to this step to a new value.
     * @param userInput Input from the user.
     */
    setText(userInput: string): TextChangeResult {
        const result: TextChangeResult = {
            input: userInput,
            helperData: {},
        };

        // Find all related elements in the design and update them. Return immediately if none are found.
        const layoutElements = textStepService.findLayoutElements(this.manager, this.step);
        // Filter any unsupported characters from the input.
        const filteredUserInput = textStepService.filterUnsupportedCharacters(userInput, layoutElements[0]?.fontData);

        // Whilst the maximum length of user input is not exceeded, set the updated input value.
        const maxLengthExceeded =
            !!this.step.data && !!this.step.data.maxLength && filteredUserInput.length > this.step.data.maxLength;
        if (!maxLengthExceeded) {
            result.input = filteredUserInput;
        }

        // Update storage.
        const regionless = layoutElements.length <= 0;
        const newStorage = regionless
            ? { inputText: filteredUserInput, text: filteredUserInput }
            : { inputText: filteredUserInput };
        this.manager.updateStorage(this.step.stepName, newStorage);

        // Calculate the text that will be metadata if no errors.
        const storage = this.manager.getStepStorage(this.step.stepName) as TextStepStorage;
        const candidateMetadata = textStepService.getProcessedInput(
            filteredUserInput,
            this.step.data,
            storage?.customiseAllText ?? false,
        );

        if (regionless) {
            // Update metadata if new text doesn't cause errors.
            if (!maxLengthExceeded) {
                this.manager.updateMetadata(this.step.stepName, {
                    text: candidateMetadata,
                });
            }
        } else {
            // Request the text step service to update the text in the design.
            const { command, errorData, helperData } = textStepService.updateInputText(
                filteredUserInput,
                layoutElements,
                this.step,
                this.manager,
            );
            command && this.manager.getCommandDispatcher()(command);

            result.helperData = helperData;
            result.errorData = errorData;

            // Update metadata if new text doesn't cause errors.
            if (!maxLengthExceeded && !errorData) {
                this.manager.updateMetadata(this.step.stepName, {
                    text: candidateMetadata,
                });
            }
        }

        return result;
    }

    /**
     * Gets the text currently applied to the elements of this step.
     */
    getText() {
        const defaultText = this.step.data.defaultText;
        const storage = this.manager.getStepStorage(this.step.stepName);
        if (defaultText && storage?.defaultCleared !== undefined && !storage?.defaultCleared) {
            return defaultText;
        }
        return this.manager.getStepStorage(this.step.stepName)?.text || "";
    }

    isReplaceable() {
        return this.step.data.userCanReplaceText;
    }

    /**
     * @returns True if the step allows new lines in the text input.
     */
    newLinesSupported() {
        return this.step.data.allowNewlines || false;
    }

    /**
     * Inform the step that now is the time to
     * clear default text if it should do so.
     * Returns a text change rwsult if a clear occurred.
     */
    clearDefaultTextIfNecessary(): TextChangeResult | undefined {
        const storage = this.manager.getStepStorage(this.step.stepName);
        if (!storage?.defaultCleared && this.step.data.deleteDefaultOnFocus) {
            const result = this.setText("");
            this.manager.updateStorage(this.step.stepName, { defaultCleared: true, inputText: "" });
            const layoutElements = textStepService.findLayoutElements(this.manager, this.step);
            textStepService.updateInputText("", layoutElements, this.step, this.manager);
            return result;
        }
    }

    hasColorPicker() {
        return this.step.data.colorPickerEnabled;
    }

    getRegions() {
        return this.step.data.regions;
    }

    /**
     * Return the maximum characters allowed for
     * this step, or undefined if there is no limit.
     */
    getCharacterLimit(): number | undefined {
        return this.step.data.maxLength;
    }

    /**
     * Return the remaining amount of characters that
     * the user is allowed to add, or undefined if there is no limit.
     */
    getCharactersRemaining(): number | undefined {
        const limit = this.step.data.maxLength;
        if (limit === undefined) {
            return undefined;
        }
        const text = this.getText();
        return limit - text.length;
    }

    setCustomColor(_color: string) {
        throw new Error("Custom color support is not yet available on text. Check back soon.");
    }

    getCustomColor(): string {
        throw new Error("Custom color support is not yet available on text. Check back soon.");
    }

    /**
     * @returns A list of colors that can be used for the text's outline.
     */
    getAvailableStrokeColors(): ColorOption[] {
        if (!this.step.data.strokeEnabled) {
            return [];
        }
        return textStepService.availableStrokeColors(this.step);
    }

    getStrokeColor(): string {
        return this.manager.getStepStorage(this.step.stepName)?.strokeColor?.browserValue || "#000000";
    }

    /**
     * Changes the stroke of text related to this step to the new stroke value.
     * @param stroke A new stroke value to use.
     */
    async setStrokeColor(stroke: ColorOption | undefined) {
        const stepElements = this.manager.getRegionElements(this.step.stepName);
        await textStepService.changeStrokeColor(this.step, stroke, stepElements, this.manager);
    }
}
